import React, { useState, useEffect } from 'react';
import moment from 'moment';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Collapse from '@material-ui/core/Collapse';

import PaginationMui from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';

import { PaginationItem } from '@mui/material';

import { useGlobal } from '../../../../../contexts/Global/context';

import Icon from '../../../../../components/Icons';
import Alert from '../../../../../components/Alert';
import Loading from '../../../../../components/Loading';
import Accordion from '../../../../../components/Accordion';
import PageLoader from '../../../../../components/PageLoader';

import { TableContent } from '../../../../../styles/styles';
import { LinkButton } from '../../../../../styles/global';
import { buscaDependentes } from '../../../../../services/beneficiario/beneficiarios';
import { carteirinhaDigitalSaude, imagemCarteirinha } from '../../../../../services/beneficiario/carteiras';

import CarteirinhaDigital from '../PdfCarteirinha';
import { generatePdf } from '../../../../../utils';

import * as S from './styles';

function SortIcon() {
    const { contrastTheme } = useGlobal();

    return (
        <S.AlignIconSort>
            <Icon name="arrow_drop_up" style={{ color: contrastTheme ? '#FFF333' : '#F49E00' }} />
            <Icon name="arrow_drop_down" style={{ marginTop: '-15px', color: contrastTheme ? '#FFF333' : '#F49E00' }} />
        </S.AlignIconSort>
    );
}

function descendingComparator(a, b, orderBy) {
    let valueA = a[orderBy];
    let valueB = b[orderBy];

    if (moment(valueA).isValid()) {
        valueA = new Date(valueA).getTime();
        valueB = new Date(valueB).getTime();
    }

    if (!valueB || !valueA) {
        return -1;
    }

    if (valueB < valueA) {
        return -1;
    }

    if (valueB > valueA) {
        return 1;
    }

    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el) => el);

    stabilizedThis.sort((a, b) => {
        const order = comparator(a, b);
        if (order !== 0) {
            return order;
        }
        return a - b;
    });

    return stabilizedThis;
}

function getButtonVisibility(props) {
    if (props.type === 'previous' && props.page === 0) {
        return 'hidden';
    }

    if (props.type === 'next' && props.page - 1 === props.qtyPages) {
        return 'hidden';
    }
    return 'visible';
}

function PaginationRounded({ onChange, qtyPages, siblingCount }) {
    const { contrastTheme } = useGlobal();

    return (
        <Stack spacing={2}>
            <PaginationMui
                renderItem={(props) => {
                    props.style = {
                        visibility: getButtonVisibility({ ...props, qtyPages }),
                    };

                    if (props.type === 'previous') {
                        return (
                            <LinkButton id="previous" {...props}>
                                <Icon name="ArrowLeft" color={contrastTheme ? '#FFF333' : '#EC6E0C'} />
                                <S.PaginationText>Anterior</S.PaginationText>
                            </LinkButton>
                        );
                    }

                    if (props.type === 'next') {
                        return (
                            <LinkButton id="forward" {...props}>
                                <S.PaginationText>Próxima</S.PaginationText>
                                <Icon name="ArrowRight" color={contrastTheme ? '#FFF333' : '#EC6E0C'} />
                            </LinkButton>
                        );
                    }

                    if (props.type.includes('ellipsis')) {
                        delete props.onClick;
                    }

                    return <PaginationItem {...props} />;
                }}
                count={qtyPages}
                onChange={onChange}
                variant="outlined"
                shape="rounded"
                siblingCount={siblingCount}
            />
        </Stack>
    );
}

function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort, columns } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <S.CustomTableHead>
            <TableRow>
                {columns.map(({ key, sortable, name }) => (
                    <React.Fragment key={key}>
                        <TableCell
                            component="th"
                            align={sortable ? 'right' : 'left'}
                            sortDirection={orderBy === key ? order : false}
                        >
                            <TableSortLabel
                                active={orderBy === key}
                                direction={orderBy === key ? order : 'asc'}
                                onClick={createSortHandler(key)}
                                IconComponent={sortable ? SortIcon : null}
                            >
                                {name}
                            </TableSortLabel>
                        </TableCell>
                    </React.Fragment>
                ))}
            </TableRow>
        </S.CustomTableHead>
    );
}

export default function DataTableMui({
    data,
    loading,
    alreadySearched,
    columns,
    firstOrderBy,
    emptyMessage,
    clickableRow,
}) {
    const { contrastTheme, dataActiveContract } = useGlobal();

    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(firstOrderBy);
    const [pagination, setPagination] = useState({ activePage: 0, showingData: [] });
    const [openRow, setOpenRow] = useState(null);
    const [itemSelect, setItemSelect] = useState({});
    const [dadosDependentes, setDadosDepentes] = useState([]);
    const [loadingPage, setLoading] = useState(false);

    const range = 2;
    const rowsPerPage = 10;
    const qtyPages = Math.ceil(data.length / rowsPerPage) || 1;
    const emptyMessageDependentes = 'Não encontramos dependentes para este beneficiário.';

    const columnsToShow = columns.filter((column) => !column.hideColumn);

    function handleChangePage(activePage) {
        if (activePage < qtyPages) {
            setPagination({
                activePage,
                showingData: [...stableSort(data, getComparator(order, orderBy))].splice(
                    activePage * rowsPerPage,
                    rowsPerPage
                ),
            });
        }
    }

    const handleRequestSort = (_, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    useEffect(() => {
        setPagination({
            activePage: 0,
            showingData: [...stableSort(data, getComparator(order, orderBy))].splice(0, rowsPerPage),
        });
    }, [data]);

    useEffect(() => {
        setPagination({
            activePage: pagination.activePage,
            showingData: [...stableSort(data, getComparator(order, orderBy))].splice(0, rowsPerPage),
        });
    }, [orderBy, order]);

    async function convertImgPng(tipoCarteirinha) {
        const payloadimagemCarteirinha = {
            canal: 'M',
            // eslint-disable-next-line object-shorthand
            tipoCarteirinha: tipoCarteirinha,
        };

        const reponseImage = await imagemCarteirinha(payloadimagemCarteirinha);
        const imagemBlob = new Blob([reponseImage], { type: 'image/jfif' });
        const imagemUrl = URL.createObjectURL(imagemBlob);
        const imagem = new Image();
        imagem.src = imagemUrl;

        const pngDataUrlPromise = new Promise((resolve, reject) => {
            imagem.onload = function () {
                const canvas = document.createElement('canvas');
                canvas.width = imagem.width;
                canvas.height = imagem.height;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(imagem, 0, 0);

                const pngDataUrl = canvas.toDataURL('image/png');

                resolve(pngDataUrl);
            };

            // eslint-disable-next-line func-names
            imagem.onerror = function () {
                reject(new Error('Erro ao carregar imagem'));
            };
        });

        return pngDataUrlPromise;
    }

    const renderRowContent = (row, column) => {
        if (column.mutation) {
            return column.mutation(column.selector(row));
        }

        return column.selector(row);
    };

    if (loading) {
        return (
            <TableContent>
                <Loading />
            </TableContent>
        );
    }

    if (!alreadySearched) {
        return <></>;
    }

    if (!loading && alreadySearched && pagination.showingData.length === 0) {
        return <Alert type="2" title={emptyMessage || 'Dados não encontrados'} />;
    }

    async function handleDependentes(row) {
        setLoading(true);
        const dependentsList = await buscaDependentes(row.numCarteira, dataActiveContract.numero);
        setDadosDepentes(dependentsList);
        setLoading(false);
    }

    const handleRowClick = (rowIndex, row) => {
        setItemSelect(row);
        setOpenRow(openRow === rowIndex ? null : rowIndex);
        if (row.tipo === 'TITULAR') {
            handleDependentes(row);
        }
    };

    async function downloadPdfCarteirinha(row) {
        const payloadBuscaCarteirinha = {
            cabecalho: {
                canal: 'M',
                codigoEmpresaOrigem: '1',
                divisaoNegocio: '1',
                empresaOrigemAtendimento: '1',
                origemContrato: '1',
                tipoContrato: '1',
                token: '1',
                unidadeNegocio: '1',
            },
            carteirinha: row.tipo === 'TITULAR' ? row.numCarteira : row.numCarteirinha,
        };

        const response = await carteirinhaDigitalSaude(payloadBuscaCarteirinha);
        const reponseImg = await convertImgPng(response.listaCarteirinhaDigitalSaude[0].tipoCarteirinha);
        if (reponseImg) {
            generatePdf(
                <CarteirinhaDigital form={response.listaCarteirinhaDigitalSaude[0]} image={reponseImg} />,
                'Carteirinha Digital'
            );
        }
    }

    return (
        <>
            <S.CustomTableContainer contrastTheme={contrastTheme}>
                <Table aria-labelledby="tableTitle">
                    <EnhancedTableHead
                        order={order}
                        columns={columnsToShow}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        rowCount={data.length}
                    />

                    <TableBody>
                        {pagination.showingData.map((row, index) => {
                            const labelId = `enhanced-table-checkbox-${index}`;
                            const isRowCurrentlyOpen = openRow === index && itemSelect.tipo === 'TITULAR';

                            return (
                                <>
                                    <S.CustomTableRow
                                        hover
                                        contrastTheme={contrastTheme}
                                        clickableRowS={clickableRow || false}
                                        onClick={() => handleRowClick(index, row)}
                                        tabIndex={-1}
                                        key={index}
                                    >
                                        {columnsToShow.map((column) => (
                                            <S.CustomTableCell
                                                component="td"
                                                id={labelId}
                                                scope="row"
                                                style={
                                                    column.customStyles &&
                                                    typeof column.customStyles === 'function' &&
                                                    column.customStyles(row)
                                                }
                                                className={column.customClassName ? column.customClassName : null}
                                            >
                                                {renderRowContent(row, column)}
                                            </S.CustomTableCell>
                                        ))}
                                    </S.CustomTableRow>
                                    {isRowCurrentlyOpen && (
                                        <S.CustomTableRow contrastTheme={contrastTheme}>
                                            <S.CustomTableCell colSpan={columnsToShow.length}>
                                                <Collapse in={isRowCurrentlyOpen} timeout="auto" unmountOnExit>
                                                    <Card>
                                                        <CardContent>
                                                            <Accordion
                                                                title="Carteirinha digital para dependentes"
                                                                hasCollapse={false}
                                                            />
                                                            {dadosDependentes?.length > 0 ? (
                                                                <>
                                                                    <div
                                                                        style={{
                                                                            padding: '10px',
                                                                        }}
                                                                    >
                                                                        {dadosDependentes.map((element) => (
                                                                            <div
                                                                                style={{
                                                                                    display: 'flex',
                                                                                    flexDirection: 'row',
                                                                                    justifyContent: 'space-around',
                                                                                    marginBottom: '14px',
                                                                                }}
                                                                            >
                                                                                <div>
                                                                                    <p>Nº da carteirinha</p>
                                                                                    <p style={{ fontSize: '16px' }}>
                                                                                        {element.numCarteirinha}
                                                                                    </p>
                                                                                </div>

                                                                                <div>
                                                                                    <p>Nome completo</p>
                                                                                    <p style={{ fontSize: '16px' }}>
                                                                                        {element.nomeCompleto}
                                                                                    </p>
                                                                                </div>
                                                                                <div>
                                                                                    <p>Grau de parentesco</p>
                                                                                    <p style={{ fontSize: '16px' }}>
                                                                                        {element.dependencia?.nome}
                                                                                    </p>
                                                                                </div>

                                                                                <div>
                                                                                    <S.CellWithIcon
                                                                                        onClick={() =>
                                                                                            downloadPdfCarteirinha(
                                                                                                element
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        <Icon
                                                                                            name="file_download"
                                                                                            tooltip="Baixar"
                                                                                            size={16}
                                                                                            color="#EC6E0C"
                                                                                        />
                                                                                        <span
                                                                                            style={{
                                                                                                color: '#EC6E0C',
                                                                                                fontWeight: 'bold',
                                                                                                textDecoration:
                                                                                                    'underline',
                                                                                                cursor: 'pointer',
                                                                                            }}
                                                                                        >
                                                                                            Baixar
                                                                                        </span>
                                                                                    </S.CellWithIcon>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <Alert type="2" title={emptyMessageDependentes} />
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                </Collapse>
                                            </S.CustomTableCell>
                                        </S.CustomTableRow>
                                    )}
                                </>
                            );
                        })}
                    </TableBody>
                </Table>
                <PageLoader loading={loadingPage} />
            </S.CustomTableContainer>
            <S.PaginationContainer className="pagination-align">
                <PaginationRounded
                    onChange={(_, value) => handleChangePage(value - 1)}
                    qtyPages={qtyPages}
                    siblingCount={range}
                />
            </S.PaginationContainer>
        </>
    );
}
